import type { IRenderer } from 'pixi.js';
import * as PIXI from 'pixi.js';
import type { Resolution } from './types';

interface RefCountedRenderer {
    current: PIXI.IRenderer<HTMLCanvasElement> | null;
    count: number;
}

interface LuminaryRendererManagerConfig {
    disabled: boolean;
    antialias: boolean;
    onContextLost: () => void;
    onContextRestored: () => void;
}

export class LuminaryRendererManager {
    static #shared: RefCountedRenderer = { current: null, count: 0 };

    /**
     * @internal
     */
    static get _sharedRenderer(): RefCountedRenderer {
        return LuminaryRendererManager.#shared;
    }

    renderer: IRenderer<HTMLCanvasElement> | null = null;

    rendererCreatedAt: number | null = null;
    webGLContextLost = false;
    webGLContextLostAt: number | null = null;
    readonly webglContextInfo = { renderer: '', vendor: '', extensions: '' };

    #disabled: boolean;
    #antialias: boolean;
    #onContextLost: () => void;
    #onContextRestored: () => void;
    #removeContextListeners: (() => void) | null = null;

    constructor(config: LuminaryRendererManagerConfig) {
        this.#disabled = config.disabled;
        this.#antialias = config.antialias;
        this.#onContextLost = config.onContextLost;
        this.#onContextRestored = config.onContextRestored;
    }

    get disabled(): boolean {
        return this.#disabled;
    }

    lazyInit(outputResolution: Resolution, referenceResolution: Resolution) {
        if (this.renderer || this.#disabled) {
            return;
        }

        const resolution = outputResolution.height / referenceResolution.height;

        LuminaryRendererManager.#shared.count += 1;
        if (LuminaryRendererManager.#shared.current === null) {
            LuminaryRendererManager.#shared.current = new PIXI.Renderer({
                width: referenceResolution.width,
                height: referenceResolution.height,
                resolution,
                antialias: this.#antialias,
                backgroundAlpha: 0,
            }) as unknown as IRenderer<HTMLCanvasElement>;
        } else if (LuminaryRendererManager.#shared.current.resolution < resolution) {
            LuminaryRendererManager.#shared.current.resolution = resolution;
        }

        this.renderer = LuminaryRendererManager.#shared.current;
        this.rendererCreatedAt = performance.now();

        this.#removeContextListeners = (() => {
            const handleContextLost = () => {
                this.webGLContextLost = true;
                this.webGLContextLostAt = performance.now();
                this.#onContextLost();
            };
            this.renderer.view.addEventListener('webglcontextlost', handleContextLost, {
                once: true,
            });
            this.renderer.view.addEventListener('webglcontextrestored', this.#onContextRestored, {
                once: true,
            });
            return () => {
                this.renderer?.view.removeEventListener('webglcontextlost', handleContextLost);
                this.renderer?.view.removeEventListener(
                    'webglcontextrestored',
                    this.#onContextRestored,
                );
            };
        })();

        (() => {
            const gl = (this.renderer as unknown as { context: { gl: WebGL2RenderingContext } })
                .context.gl;
            const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
            if (!debugInfo) {
                return;
            }
            this.webglContextInfo.renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
            this.webglContextInfo.vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
            this.webglContextInfo.extensions = (gl.getSupportedExtensions() ?? []).join(', ');
        })();
    }

    growIfNeeded(outputW: number, outputH: number) {
        if (!this.renderer) {
            return;
        }
        const rendererIsLargeEnough =
            this.renderer.width >= outputW && this.renderer.height >= outputH;
        if (rendererIsLargeEnough) {
            return;
        }
        const { width, height, resolution } = this.renderer;
        this.renderer.resize(
            Math.max(width, outputW) / resolution,
            Math.max(height, outputH) / resolution,
        );
    }

    destroy() {
        if (!this.renderer) {
            return;
        }
        this.#removeContextListeners?.();
        this.#removeContextListeners = null;
        this.renderer = null;

        LuminaryRendererManager.#shared.count -= 1;
        if (LuminaryRendererManager.#shared.count > 0) {
            return;
        }
        LuminaryRendererManager.#shared.current?.destroy(true);
        LuminaryRendererManager.#shared.current = null;
        LuminaryRendererManager.#shared.count = 0;
    }
}
